import { QueryClient, QueryKey } from "@tanstack/react-query";
import { OrderResponse, OrderStateResponse, OrderStateKey, OrderUpdate } from "@sdk/shared/types/order.types";
import { P2P_ORDERS_QUERY_KEY, ORDER_QUERY_KEY } from "@shared/src/lib/queryKeys";
import { OrdersQueryParams, UseOrdersQueryResponse } from "@shared/src/components/modules/orders/use-p2p-orders";
import { IN_PROGRESS_STATES } from "@shared/src/constants";
import { queryClient } from "@shared/src/providers/queryClientProvider";
import { readContractQueryKey } from "wagmi/query";
import Erc20Permit from "@shared/src/lib/abis/Erc20Permit";
import { useHostStore } from "../stores/useHostStore";

type OrderQueryResponse = { data: OrderResponse };
type OrderStateQueryResponse = { data: OrderStateResponse };
type P2POrdersQueryResponse = { orders: OrderResponse[]; totalItems: number; hasMore: boolean };

export interface CacheUpdateOptions {
  queryClient: QueryClient;
  orderId: string;
  newOrder?: OrderResponse;
  newState?: OrderStateResponse;
}

export interface P2POrdersCacheUpdateOptions {
  queryClient: QueryClient;
  newOrder: OrderResponse;
  params?: OrdersQueryParams;
}

export type updateOrderParams =
  | {
      newOrder: OrderResponse;
      newState?: never;
    }
  | {
      newOrder?: never;
      newState: OrderUpdate;
    };

export function updateOrderInQueries({ newState, newOrder }: updateOrderParams) {
  const orderId = newOrder?.id || newState!.orderId;
  const activeAddress = useHostStore.getState().sdk?.auth.getAddress();
  const activeChainId = useHostStore.getState().activeChainId;
  if (!activeAddress || !activeChainId) {
    console.error({
      activeAddress,
      activeChainId,
    });
    throw new Error("Active address or chain id not found in updateOrderInQueries");
  }
  const updateOne = <T extends OrderResponse | undefined>(oldOrder: T) => {
    if (!oldOrder) {
      return oldOrder;
    }
    if (newOrder?.state === "released" || newState?.state === "released") {
      queryClient.invalidateQueries({
        queryKey: readContractQueryKey({
          abi: Erc20Permit,
          address: oldOrder.token.address,
          functionName: "balanceOf",
          args: [activeAddress],
          chainId: activeChainId,
        }),
      });
    }
    if (newState && oldOrder.id === orderId) {
      return { ...oldOrder, stateHistory: [...(oldOrder.stateHistory || []), newState], state: newState.state };
    }
    if (newOrder && oldOrder.id === orderId) {
      return newOrder;
    }
    return oldOrder;
  };

  const updater = (oldData?: UseOrdersQueryResponse): UseOrdersQueryResponse | undefined => {
    if (!oldData?.orders) {
      return;
    }

    return {
      ...oldData,
      orders: oldData.orders.map(updateOne),
    };
  };
  const remover = (oldData?: UseOrdersQueryResponse): UseOrdersQueryResponse | undefined => {
    if (!oldData?.orders) {
      return;
    }
    const newOrders = oldData.orders.filter(order => order.id !== orderId);

    return {
      ...oldData,
      totalItems: oldData.totalItems - (oldData.orders.length - newOrders.length),
      orders: newOrders,
    };
  };

  const orderQueryKey = ORDER_QUERY_KEY(orderId);
  const one = queryClient.setQueryData(orderQueryKey, updateOne);

  const all = queryClient.setQueriesData({ queryKey: P2P_ORDERS_QUERY_KEY({}) }, updater);
  const state = newOrder?.state || newState?.state;
  if (!IN_PROGRESS_STATES.includes(state as any)) {
    const remove = queryClient.setQueriesData({ queryKey: P2P_ORDERS_QUERY_KEY({ state: "in_progress" }) }, remover);
    return { one, all, remove };
  }
  return { one, all };
}

export function addOrderToInProgressQueries(newOrder: OrderResponse) {
  const queryKey = P2P_ORDERS_QUERY_KEY({ state: "in_progress" });
  if (!IN_PROGRESS_STATES.includes(newOrder.state as any)) return;
  const addOrder = (oldData?: UseOrdersQueryResponse): UseOrdersQueryResponse | undefined => {
    if (!oldData?.orders) {
      queryClient.fetchQuery({ queryKey });
      return undefined;
    }
    const exists = oldData.orders.find(data => data.id === newOrder.id);
    if (!exists) {
      return {
        ...oldData,
        orders: [newOrder, ...oldData.orders],
        totalItems: oldData.totalItems + 1,
      };
    }
    return oldData;
  };

  queryClient.setQueriesData({ queryKey }, addOrder);
}
